import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  BookOpen, 
  Plus, 
  Edit2, 
  Trash2, 
  Save, 
  X,
  Loader2,
  Eye,
  EyeOff,
  Video,
  GripVertical,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import MainLayout from '@/components/layout/MainLayout';

const CATEGORIES = [
  { id: 'getting_started', name: 'Primeros Pasos' },
  { id: 'funnels', name: 'Crear Funnels' },
  { id: 'landings', name: 'Landing Pages' },
  { id: 'creatives', name: 'Creativos' },
  { id: 'integrations', name: 'Integraciones' },
  { id: 'billing', name: 'Facturación' },
  { id: 'advanced', name: 'Avanzado' }
];

const AdminTutoriales = () => {
  const { api } = useAuth();
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Editor state
  const [showEditor, setShowEditor] = useState(false);
  const [editingTutorial, setEditingTutorial] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'getting_started',
    content: '',
    video_url: '',
    order: 0,
    is_published: true
  });

  // Filters
  const [filterCategory, setFilterCategory] = useState('');

  useEffect(() => {
    loadTutorials();
  }, []);

  const loadTutorials = async () => {
    try {
      const response = await api.get('/tutorials/admin/all');
      setTutorials(response.data.tutorials || []);
    } catch (error) {
      toast.error('Error al cargar tutoriales');
    } finally {
      setLoading(false);
    }
  };

  const openEditor = (tutorial = null) => {
    if (tutorial) {
      setEditingTutorial(tutorial);
      setForm({
        title: tutorial.title,
        description: tutorial.description,
        category: tutorial.category,
        content: tutorial.content,
        video_url: tutorial.video_url || '',
        order: tutorial.order || 0,
        is_published: tutorial.is_published
      });
    } else {
      setEditingTutorial(null);
      setForm({
        title: '',
        description: '',
        category: 'getting_started',
        content: '',
        video_url: '',
        order: tutorials.length,
        is_published: true
      });
    }
    setShowEditor(true);
  };

  const closeEditor = () => {
    setShowEditor(false);
    setEditingTutorial(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title || !form.description || !form.content) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }

    setSaving(true);
    try {
      if (editingTutorial) {
        await api.put(`/tutorials/admin/${editingTutorial.id}`, form);
        toast.success('Tutorial actualizado');
      } else {
        await api.post('/tutorials/admin/create', form);
        toast.success('Tutorial creado');
      }
      await loadTutorials();
      closeEditor();
    } catch (error) {
      toast.error('Error al guardar tutorial');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tutorialId) => {
    if (!window.confirm('¿Eliminar este tutorial?')) return;
    
    try {
      await api.delete(`/tutorials/admin/${tutorialId}`);
      toast.success('Tutorial eliminado');
      await loadTutorials();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const togglePublish = async (tutorial) => {
    try {
      await api.put(`/tutorials/admin/${tutorial.id}`, {
        is_published: !tutorial.is_published
      });
      await loadTutorials();
      toast.success(tutorial.is_published ? 'Tutorial ocultado' : 'Tutorial publicado');
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const filteredTutorials = filterCategory 
    ? tutorials.filter(t => t.category === filterCategory)
    : tutorials;

  // Group tutorials by category
  const groupedTutorials = filteredTutorials.reduce((acc, tutorial) => {
    if (!acc[tutorial.category]) {
      acc[tutorial.category] = [];
    }
    acc[tutorial.category].push(tutorial);
    return acc;
  }, {});

  if (loading) {
    return (
      <MainLayout>
        <div className="flex-1 p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-brand animate-spin" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <main className="flex-1 p-8 overflow-auto">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-text-main flex items-center gap-3">
                <BookOpen className="w-7 h-7 text-brand" />
                Gestión de Tutoriales
              </h1>
              <p className="text-text-muted mt-1">
                {tutorials.length} tutoriales • {tutorials.filter(t => t.is_published).length} publicados
              </p>
            </div>
            <Button 
              onClick={() => openEditor()}
              className="bg-brand hover:bg-brand/90 text-black"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Tutorial
            </Button>
          </div>

          {/* Filter by category */}
          <div className="mb-6 flex gap-2 flex-wrap">
            <Button
              variant={!filterCategory ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterCategory('')}
              className={!filterCategory ? 'bg-brand text-black' : ''}
            >
              Todos ({tutorials.length})
            </Button>
            {CATEGORIES.map(cat => {
              const count = tutorials.filter(t => t.category === cat.id).length;
              return (
                <Button
                  key={cat.id}
                  variant={filterCategory === cat.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterCategory(cat.id)}
                  className={filterCategory === cat.id ? 'bg-brand text-black' : ''}
                >
                  {cat.name} ({count})
                </Button>
              );
            })}
          </div>

          {/* Tutorials List */}
          {Object.entries(groupedTutorials).length === 0 ? (
            <div className="text-center py-16 bg-surface rounded-xl border border-border-subtle">
              <BookOpen className="w-12 h-12 text-text-muted mx-auto mb-4 opacity-50" />
              <p className="text-text-muted">No hay tutoriales creados</p>
              <Button 
                onClick={() => openEditor()}
                className="mt-4 bg-brand hover:bg-brand/90 text-black"
              >
                Crear el primero
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedTutorials).map(([categoryId, categoryTutorials]) => {
                const category = CATEGORIES.find(c => c.id === categoryId);
                return (
                  <div key={categoryId} className="bg-surface rounded-xl border border-border-subtle overflow-hidden">
                    <div className="px-6 py-4 border-b border-border-subtle bg-app-bg/50">
                      <h3 className="font-semibold text-text-main">
                        {category?.name || categoryId}
                        <span className="ml-2 text-text-muted font-normal text-sm">
                          ({categoryTutorials.length})
                        </span>
                      </h3>
                    </div>
                    <div className="divide-y divide-border-subtle">
                      {categoryTutorials.sort((a, b) => a.order - b.order).map((tutorial) => (
                        <div 
                          key={tutorial.id} 
                          className="px-6 py-4 flex items-center gap-4 hover:bg-app-bg/30 transition-colors"
                        >
                          <GripVertical className="w-4 h-4 text-text-muted cursor-grab" />
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-text-main truncate">
                                {tutorial.title}
                              </h4>
                              {!tutorial.is_published && (
                                <span className="px-2 py-0.5 text-xs bg-yellow-500/20 text-yellow-400 rounded">
                                  Borrador
                                </span>
                              )}
                              {tutorial.video_url && (
                                <Video className="w-4 h-4 text-brand" />
                              )}
                            </div>
                            <p className="text-sm text-text-muted truncate">
                              {tutorial.description}
                            </p>
                          </div>

                          <div className="flex items-center gap-2">
                            <span className="text-xs text-text-muted">
                              {tutorial.views || 0} vistas
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => togglePublish(tutorial)}
                              className="text-text-muted hover:text-text-main"
                            >
                              {tutorial.is_published ? (
                                <Eye className="w-4 h-4" />
                              ) : (
                                <EyeOff className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditor(tutorial)}
                              className="text-text-muted hover:text-brand"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(tutorial.id)}
                              className="text-text-muted hover:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-2xl border border-border-subtle w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-border-subtle flex items-center justify-between">
              <h3 className="text-lg font-semibold text-text-main">
                {editingTutorial ? 'Editar Tutorial' : 'Nuevo Tutorial'}
              </h3>
              <Button variant="ghost" size="sm" onClick={closeEditor}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Título *</Label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Cómo crear tu primer funnel"
                    className="bg-app-bg border-border-subtle"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Categoría *</Label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full h-10 px-3 rounded-md bg-app-bg border border-border-subtle text-text-main"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Descripción corta *</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Aprende a configurar un embudo de ventas desde cero"
                  className="bg-app-bg border-border-subtle"
                />
              </div>

              <div className="space-y-2">
                <Label>URL del video (opcional)</Label>
                <Input
                  value={form.video_url}
                  onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                  placeholder="https://www.youtube.com/embed/..."
                  className="bg-app-bg border-border-subtle"
                />
                <p className="text-xs text-text-muted">
                  Usa la URL de embed de YouTube o Vimeo
                </p>
              </div>

              <div className="space-y-2">
                <Label>Contenido (Markdown) *</Label>
                <Textarea
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  placeholder="## Introducción&#10;&#10;En este tutorial aprenderás..."
                  className="bg-app-bg border-border-subtle min-h-[300px] font-mono text-sm"
                />
                <p className="text-xs text-text-muted">
                  Soporta Markdown: ## títulos, **negrita**, - listas, etc.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Orden</Label>
                  <Input
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                    className="bg-app-bg border-border-subtle"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <div className="flex items-center gap-4 h-10">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.is_published}
                        onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                        className="w-4 h-4 rounded border-border-subtle bg-app-bg"
                      />
                      <span className="text-text-main">Publicado</span>
                    </label>
                  </div>
                </div>
              </div>
            </form>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-border-subtle flex justify-end gap-3">
              <Button variant="outline" onClick={closeEditor}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={saving}
                className="bg-brand hover:bg-brand/90 text-black"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default AdminTutoriales;
